import { useEffect, useRef, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { trackArticleClose } from '../models/content/analytics';

interface ArticleCloseTrackingOptions {
  contentId: number | undefined;
  enabled: boolean;
  wordCount?: number | null;
}

interface ScrollMetrics {
  scrollStartPct: number;
  scrollStopPct: number;
  maxScrollDepthPct: number;
  scrollCount: number;
}

interface ActiveDurationTracker {
  activeDurationMs: number;
  lastActiveTimestamp: number | null;
}

const getScrollPct = (): number => {
  const scrollTop = window.scrollY || document.documentElement.scrollTop;
  const scrollHeight = document.documentElement.scrollHeight;
  const clientHeight = document.documentElement.clientHeight;
  const maxScroll = scrollHeight - clientHeight;

  if (maxScroll <= 0) return 100;
  const pct = Math.round((scrollTop / maxScroll) * 100);
  return Math.min(100, Math.max(0, pct));
};

export const useArticleCloseTracking = ({
  contentId,
  enabled,
  wordCount,
}: ArticleCloseTrackingOptions) => {
  const openTimestamp = useRef<number>(Date.now());
  const activeDuration = useRef<ActiveDurationTracker>({
    activeDurationMs: 0,
    lastActiveTimestamp: Date.now(),
  });
  const scrollMetrics = useRef<ScrollMetrics>({
    scrollStartPct: 0,
    scrollStopPct: 0,
    maxScrollDepthPct: 0,
    scrollCount: 0,
  });
  const hasFired = useRef(false);
  const contentIdRef = useRef(contentId);
  contentIdRef.current = contentId;

  const pauseActiveDuration = useCallback(() => {
    const tracker = activeDuration.current;
    if (tracker.lastActiveTimestamp !== null) {
      tracker.activeDurationMs += Date.now() - tracker.lastActiveTimestamp;
      tracker.lastActiveTimestamp = null;
    }
  }, []);

  const resumeActiveDuration = useCallback(() => {
    const tracker = activeDuration.current;
    if (tracker.lastActiveTimestamp === null) {
      tracker.lastActiveTimestamp = Date.now();
    }
  }, []);

  const fireArticleClose = useCallback(() => {
    if (hasFired.current || !contentIdRef.current) return;
    hasFired.current = true;

    // Finalize active duration
    pauseActiveDuration();

    const metrics = scrollMetrics.current;
    const tracker = activeDuration.current;
    const articleViewDuration = Math.round(
      (Date.now() - openTimestamp.current) / 1000
    );
    trackArticleClose(contentIdRef.current, {
      article_view_duration: articleViewDuration,
      active_duration_ms: tracker.activeDurationMs,
      article_scroll_start_stop: `${metrics.scrollStartPct}-${metrics.scrollStopPct}`,
      max_scroll_depth_pct: metrics.maxScrollDepthPct,
      article_scroll_numbers: metrics.scrollCount,
      word_count: wordCount ?? undefined,
    });
  }, [pauseActiveDuration, wordCount]);

  useEffect(() => {
    if (!enabled || !contentId) return;

    // Reset state on mount / contentId change
    openTimestamp.current = Date.now();
    hasFired.current = false;
    activeDuration.current = {
      activeDurationMs: 0,
      lastActiveTimestamp: Date.now(),
    };

    const initialScrollPct = getScrollPct();
    scrollMetrics.current = {
      scrollStartPct: initialScrollPct,
      scrollStopPct: initialScrollPct,
      maxScrollDepthPct: initialScrollPct,
      scrollCount: 0,
    };

    let lastScrollY = window.scrollY;

    const handleScroll = debounce(
      () => {
        const currentScrollY = window.scrollY;
        if (currentScrollY !== lastScrollY) {
          const metrics = scrollMetrics.current;
          metrics.scrollCount += 1;

          const pct = getScrollPct();
          metrics.scrollStopPct = pct;
          if (pct > metrics.maxScrollDepthPct) {
            metrics.maxScrollDepthPct = pct;
          }
          lastScrollY = currentScrollY;
        }
      },
      150,
      { leading: true, maxWait: 300 }
    );

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        pauseActiveDuration();
      } else {
        resumeActiveDuration();
      }
    };

    const handleBeforeUnload = () => {
      fireArticleClose();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);

      // Flush pending scroll update before firing close event
      handleScroll.flush();
      handleScroll.cancel();

      // Fire on unmount (navigation away)
      fireArticleClose();
    };
  }, [
    contentId,
    enabled,
    fireArticleClose,
    pauseActiveDuration,
    resumeActiveDuration,
  ]);
};
